import type { NormalisedTrack } from '@/lib/parse';
import { normaliseTrack, ParseError, parseTrackFile } from '@/lib/parse';

/**
 * One file in, one result out — and never a thrown exception.
 *
 * Bulk import is the whole point of this phase: fifty files in one drop, with a
 * per-file result list. That only works if a single unreadable file cannot take
 * the batch down with it, so every failure comes back as a value. It is also
 * the reason this function is pure and takes bytes: the same code runs on the
 * main thread and inside a worker, and neither knows which it is.
 */

export interface ParsedFileOk {
  ok: true;
  filename: string;
  normalised: NormalisedTrack;
  /** What the parser recognised, shown next to the filename. */
  notes: string[];
}

export interface ParsedFileFailed {
  ok: false;
  filename: string;
  message: string;
  detail: string[];
}

export type ParsedFileResult = ParsedFileOk | ParsedFileFailed;

export interface ParseFileOptions {
  /** Fixed clock, so a test is not a moving target. */
  importedAt?: string;
  /** Fixed id, likewise. */
  id?: string;
}

export function parseFileToTrack(
  filename: string,
  bytes: Uint8Array,
  options: ParseFileOptions = {},
): ParsedFileResult {
  try {
    const parsed = parseTrackFile(filename, bytes);
    const normalised = normaliseTrack(parsed, {
      sourceRef: filename,
      ...(options.importedAt === undefined ? {} : { importedAt: options.importedAt }),
      ...(options.id === undefined ? {} : { id: options.id }),
    });
    return { ok: true, filename, normalised, notes: parsed.notes };
  } catch (cause) {
    if (cause instanceof ParseError) {
      return { ok: false, filename, message: cause.message, detail: cause.detail ?? [] };
    }
    /*
     * An unexpected failure is a parser bug, not a bad file. It still has to
     * come back as a result rather than a throw, but the message says so — a
     * pilot told "this file is invalid" about a file that is fine will go and
     * re-export it for nothing.
     */
    return {
      ok: false,
      filename,
      message: 'This file could not be read. The reason was not one the importer expects.',
      detail: [cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause)],
    };
  }
}
